
'use client';

import { useState, useRef, ChangeEvent } from 'react';
import { Copy, Check, Upload, X, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { base64Content } from '../../data/base64-content';
import Link from 'next/link';

export default function Base64Tool() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [isEncoding, setIsEncoding] = useState(true);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  const handleEncode = () => {
    try {
      if (!input.trim()) {
        toast.error('Please enter some text to encode');
        return;
      }
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      toast.success('Text encoded successfully!');
    } catch (err) {
      toast.error('Error encoding text');
    }
  };

  const handleDecode = () => {
    try {
      if (!input.trim()) {
        toast.error('Please enter some text to decode');
        return;
      }
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      toast.success('Text decoded successfully!');
    } catch (err) {
      toast.error('Invalid Base64 string');
    }
  };

  const handleFileUpload = (file: File) => {
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      if (e.target?.result) {
        const base64String = (e.target.result as string).split(',')[1];
        setInput(base64String);
        setOutput('');
      }
    };

    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (

        <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ToolPageIntro title="Base64 Encoder / Decoder">
          Encode text or binary data to Base64, or decode Base64 back to plain text. Use it for data URIs in HTML/CSS, API payloads, email attachments, or any system that requires ASCII-safe encoding. No sign-up or installation—paste or upload and get results instantly.
        </ToolPageIntro>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Tool Interface */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Encode or Decode Base64</h2>

              <div className="mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mode"
                      checked={isEncoding}
                      onChange={() => setIsEncoding(true)}
                      className="mr-2"
                    />
                    <span className="text-gray-700">Encode</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mode"
                      checked={!isEncoding}
                      onChange={() => setIsEncoding(false)}
                      className="mr-2"
                    />
                    <span className="text-gray-700">Decode</span>
                  </label>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="input" className="block text-sm font-medium text-gray-700 mb-2">
                      {isEncoding ? 'Text to Encode' : 'Base64 to Decode'}
                    </label>
                    {fileName && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded mb-2">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-sm text-gray-700">{fileName}</span>
                        </div>
                        <button
                          onClick={() => {
                            setFileName('');
                            setInput('');
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    <textarea
                      id="input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={isEncoding ? 'Enter text to encode...' : 'Paste Base64 string to decode...'}
                      spellCheck="false"
                    />
                  </div>

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                      }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center">
                      <Upload className="h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {isEncoding ? 'File will be encoded to Base64' : 'Only for encoding, not for decoding'}
                      </p>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={isEncoding ? handleEncode : handleDecode}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      {isEncoding ? 'Encode' : 'Decode'}
                    </button>
                    <button
                      onClick={() => {
                        setInput('');
                        setOutput('');
                        setFileName('');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="output" className="block text-sm font-medium text-gray-700">
                    {isEncoding ? 'Encoded Base64' : 'Decoded Text'}
                  </label>
                  <button
                    onClick={() => copyToClipboard(output)}
                    disabled={!output}
                    className={`p-1.5 rounded ${output
                      ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      : 'text-gray-300 cursor-not-allowed'
                      }`}
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="code-block h-64">
                  <code>
                    {output || 'Your result will appear here...'}
                  </code>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-2">How to use the Base64 Tool</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-blue-700 mb-1">Encoding:</h4>
                    <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                      <li>Enter text in the input field or upload a file</li>
                      <li>Click "Encode"</li>
                      <li>Copy the encoded result</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-700 mb-1">Decoding:</h4>
                    <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                      <li>Paste a Base64 string in the input field</li>
                      <li>Click "Decode"</li>
                      <li>View or copy the decoded text</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Informational Content */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <InformationalContent
                title={base64Content.title}
                description={base64Content.description}
                content={base64Content.content}
                faqs={base64Content.faqs}
                relatedTools={base64Content.relatedTools}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Tips Card */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/80 p-5 text-slate-900 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-50">
              <h3 className="mb-3 text-sm font-semibold">Quick Tips</h3>
              <ul className="space-y-2 text-xs">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Base64 increases file size by about 33%</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Not suitable for large files due to size increase</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-emerald-500/90 text-[10px] text-white">
                    ✓
                  </span>
                  <span>Use for small files and text encoding</span>
                </li>
              </ul>
            </div>

            {/* Related Tools Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Tools</h3>
              <ul className="space-y-3">
                {base64Content.relatedTools.map((tool, index) => (
                  <li key={index}>
                    <Link
                      href={tool.url}
                      className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {tool.name}
                    </Link>
                    <p className="text-sm text-gray-600 ml-7">{tool.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
